import { Theme } from '@mui/material';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { type JSX } from 'react';
import ClassicLayerImage from '../../img/classic-layer.jpg';
import ParcelBoundariesImage from '../../img/parcel-boundaries-layer.jpg';
import SatelliteLayerImage from '../../img/satellite-layer.jpg';
import { BaseLayers, ToggleLayers } from '../../types';
import { GeofenceController } from '../map/layers';
import ClassicLayer from '../map/layers/classic-layer';
import { classicMapStyle } from '../map/layers/classic-vector-map-style';
import { IncidentLayer } from '../map/layers/incident-layer';
import ParcelBoundaries from '../map/layers/parcel-boundaries';
import { parcelBoundariesStyles } from '../map/layers/parcel-boundaries-map-style';
import { PointOfInterestController } from '../map/layers/point-of-interest';
import SatelliteLayer from '../map/layers/satellite-layer';
import { getSatelliteMapStyle } from '../map/layers/satellite-vector-map-style';
import { TrafficLayer } from '../map/layers/traffic-layer';
import { TransportLayer } from '../map/layers/transport-layer';

const TEMP_VECTOR_LAYER_FLAG = 'track-overlay-vector-layer-on-satellite-map-temporary-feature';

export const TemplateLayerType = {
  CLASSIC: 'classic',
  PARCEL_BOUNDARIES: 'parcel-boundaries',
  SATELLITE: 'satellite',

  THINGS: 'things',
  TRAFFIC: 'traffic',
  TRANSPORT: 'transport',
  INCIDENTS: 'incidents',

  GEOFENCES: 'geofences',
  POINT_OF_INTEREST: 'point-of-interest',
} as const;

export type TemplateLayerTypeValue = typeof TemplateLayerType[keyof typeof TemplateLayerType];

const BASE_LAYER_TYPE_MAP: Record<string, BaseLayers> = {
  [TemplateLayerType.CLASSIC]: BaseLayers.classic,
  [TemplateLayerType.PARCEL_BOUNDARIES]: BaseLayers.parcelBoundaries,
  [TemplateLayerType.SATELLITE]: BaseLayers.satellite,
};

const TOGGLE_LAYER_TYPE_MAP: Record<string, ToggleLayers> = {
  [TemplateLayerType.TRAFFIC]: ToggleLayers.traffic,
  [TemplateLayerType.TRANSPORT]: ToggleLayers.transport,
  [TemplateLayerType.INCIDENTS]: ToggleLayers.incidents,
  [TemplateLayerType.GEOFENCES]: ToggleLayers.geofences,
  [TemplateLayerType.POINT_OF_INTEREST]: ToggleLayers.pointOfInterest,
};

export const mapTemplateTypeToBaseLayer = (type: string): BaseLayers | undefined => {
  return BASE_LAYER_TYPE_MAP[type];
};

export const mapTemplateTypeToToggleLayer = (type: string): ToggleLayers | undefined => {
  return TOGGLE_LAYER_TYPE_MAP[type];
};

export const getLayerComponentForType = (type: string, _flags?: LDFlagSet): JSX.Element | undefined => {
  switch (type) {
    case TemplateLayerType.CLASSIC:
      return <ClassicLayer />;
    case TemplateLayerType.PARCEL_BOUNDARIES:
      return <ParcelBoundaries />;
    case TemplateLayerType.SATELLITE:
      return <SatelliteLayer />;

    case TemplateLayerType.TRAFFIC:
      return <TrafficLayer />;
    case TemplateLayerType.TRANSPORT:
      return <TransportLayer />;
    case TemplateLayerType.INCIDENTS:
      return <IncidentLayer />;

    case TemplateLayerType.GEOFENCES:
      return <GeofenceController />;
    case TemplateLayerType.POINT_OF_INTEREST:
      return <PointOfInterestController />;
    default:
      return undefined;
  }
};

export const getBaseLayerMapStyle = (type: string, flags?: LDFlagSet): ((theme: Theme, apiKey: string, enableSource?: boolean) => Record<string, unknown>) | undefined => {
  switch (type) {
    case TemplateLayerType.CLASSIC:
      return classicMapStyle;
    case TemplateLayerType.PARCEL_BOUNDARIES:
      return parcelBoundariesStyles;
    case TemplateLayerType.SATELLITE:
      return getSatelliteMapStyle(flags && !!flags[TEMP_VECTOR_LAYER_FLAG]);
    default:
      return undefined;
  }
};

const BASE_LAYER_IMAGE_MAP: Record<string, string> = {
  [TemplateLayerType.CLASSIC]: ClassicLayerImage,
  [TemplateLayerType.PARCEL_BOUNDARIES]: ParcelBoundariesImage,
  [TemplateLayerType.SATELLITE]: SatelliteLayerImage,
};

export const getBaseLayerImage = (type: string): JSX.Element | undefined => {
  const imageSrc = BASE_LAYER_IMAGE_MAP[type];

  if (imageSrc) {
    return <img height="auto" src={imageSrc} style={{ top: 0, left: 0, position: 'absolute' }} width="100%" />;
  }

  return undefined;
};
